"use client";

import { Button } from "@/shared/components/common/button";
import Flex from "@/shared/components/common/Flex";
import Typography from "@/shared/components/common/Typography";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/shared/components/common/Accordion";
import data from "@/public/data/flight/passengers.json";
import type { ReactNode } from "react";
import { Pulse } from "@/shared/components/common/Animations";
import { useGlobalStore } from "@/shared/stores/global-store/GlobalStoreProvider";
import Skeleton from "@/shared/components/common/Skeleton";
import { cn } from "@/shared/libs/commonUtils";

import { Warning } from "@phosphor-icons/react";

interface SubmitCardProps {
  children?: ReactNode;
  content: ReactNode;
  totalPrice: number;
  isLoading: boolean;
  buttonText?: string | null;
  termsAndConditionMessage?: string | ReactNode;
  showPeakSeasonNotice?: boolean;

  validationError?: boolean;
  onSubmit?: () => void;
}

export default function SubmitCard({
  children,
  content,
  buttonText,
  totalPrice,
  termsAndConditionMessage,
  showPeakSeasonNotice = false,
  isLoading,

  validationError,
  onSubmit,
}: SubmitCardProps) {
  const {
    submitInfo: { submit, currency, total },
  } = data;

  const { isLoginModalOn } = useGlobalStore((state) => state);

  return (
    <Accordion
      type="single"
      collapsible
      className={`w-full bg-background !fixed bottom-0 left-0 right-0 z-10! lg:hidden ${
        isLoginModalOn && "hidden!"
      }`}
    >
      <AccordionItem value="total" className="shadow-1">
        <AccordionContent className="w-full bg-background! !py-2 px-4 border-t border-border">
          {content}
        </AccordionContent>
        <Flex className="w-full border-t border-border p-3">
          <Flex className="w-full flex-col">
            <Flex className="flex-col items-center">
              {termsAndConditionMessage && (
                <Flex className="items-center">
                  <Typography variant="caption" className="">
                    {termsAndConditionMessage}
                  </Typography>
                </Flex>
              )}
              <Flex className="w-full items-center justify-between">
                <AccordionTrigger className="gap-x-2 !py-2">
                  {total}
                </AccordionTrigger>
                <Flex className="items-center gap-x-2">
                  {isLoading ? (
                    <Skeleton className="w-28 py-2 rounded-[4px]" />
                  ) : (
                    <Typography
                      variant="body1"
                      className="text-primary font-semibold"
                    >
                      {totalPrice.toLocaleString()}
                    </Typography>
                  )}
                  <Typography
                    variant="body1"
                    className="text-primary font-medium"
                  >
                    {currency}
                  </Typography>
                </Flex>
              </Flex>

              {showPeakSeasonNotice && (
                <Flex className="items-center mb-2">
                  <Warning
                    weight="fill"
                    className="w-6 h-6 ml-2 text-primary-90"
                  />
                  <Typography variant="caption">
                    در ایام پیک (در ایام خاص) رزرو یک شبه فقط با هماهنگی قبلی مورد
                    تایید است.
                  </Typography>
                </Flex>
              )}

              {children || (
                <Button
                  className={cn(
                    "w-full py-5 rounded-full",
                    validationError ? "bg-destructive" : "bg-primary"
                  )}
                  onClick={onSubmit}
                  disabled={totalPrice === 0}
                >
                  {isLoading ? (
                    <Pulse />
                  ) : (
                    <Typography variant="body1" className="font-medium">
                      {buttonText ?? submit}
                    </Typography>
                  )}
                </Button>
              )}
            </Flex>
          </Flex>
        </Flex>
      </AccordionItem>
    </Accordion>
  );
}
